import { useEffect, useMemo } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, Phone, ArrowRight, Home } from "lucide-react";

export default function ThankYou() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const submittedData = useMemo(() => {
    if (location.state?.data) {
      return location.state.data;
    }

    const cached = sessionStorage.getItem("latestEnquiry");
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch {
        return {};
      }
    }

    return {};
  }, [location.state]);
  const wasSubmitted = searchParams.get("submitted") === "1" || Boolean(submittedData.name);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!wasSubmitted) {
    return (
      <div
        className="min-h-screen bg-vastu-cream paper-texture flex flex-col items-center justify-center pt-20"
        data-testid="thank-you-page"
      >
        <div className="relative max-w-2xl mx-auto px-6 py-20 text-center">
          <h1 className="font-heading text-4xl font-light mb-4" style={{ color: "#2D2A26" }}>
            Thank You
          </h1>
          <p className="font-body text-lg text-vastu-mutedFg mb-8">
            Your enquiry has been received successfully.
          </p>
          <Link to="/get-floor-plan" className="btn-primary" data-testid="new-enquiry-btn">
            Submit Enquiry
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-vastu-cream paper-texture flex flex-col items-center justify-center pt-20"
      data-testid="thank-you-page"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-5"
          style={{ background: "radial-gradient(circle, #D27C2C, transparent)" }} />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-5"
          style={{ background: "radial-gradient(circle, #3A5A40, transparent)" }} />
      </div>

      <div className="relative max-w-2xl mx-auto px-6 py-20 text-center">
        {/* Success Icon */}
        <div className="flex justify-center mb-8 animate-fade-in-up">
          <div
            className="w-24 h-24 rounded-full flex items-center justify-center animate-float"
            style={{ background: "linear-gradient(135deg, #D27C2C, #E9C46A)" }}
          >
            <CheckCircle2 size={48} className="text-white" />
          </div>
        </div>

        {/* Heading */}
        <h1
          className="font-heading text-5xl md:text-6xl font-light mb-4 animate-fade-in-up delay-100"
          style={{ color: "#2D2A26" }}
          data-testid="thank-you-heading"
        >
          Namaste!
          <br />
          <span className="gradient-text">Your Request is Received</span>
        </h1>

        <p
          className="font-body text-lg text-vastu-mutedFg mb-10 leading-relaxed animate-fade-in-up delay-200"
        >
          Thank you, <strong style={{ color: "#2D2A26" }}>{submittedData.name || "valued customer"}</strong>!
          Our Vastu experts will review your plot details and prepare a customized,
          Vastu-compliant floor plan for you.
        </p>

        {/* Info Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10 animate-fade-in-up delay-300">
          <div className="vastu-card text-left">
            <div className="flex items-center gap-3 mb-2">
              <Clock size={20} style={{ color: "#D27C2C" }} />
              <span className="font-body font-semibold text-vastu-charcoal">Response Time</span>
            </div>
            <p className="font-body text-sm text-vastu-mutedFg">
              Our team will reach out within <strong>24-48 business hours</strong>
            </p>
          </div>
          <div className="vastu-card text-left">
            <div className="flex items-center gap-3 mb-2">
              <Phone size={20} style={{ color: "#3A5A40" }} />
              <span className="font-body font-semibold text-vastu-charcoal">Next Step</span>
            </div>
            <p className="font-body text-sm text-vastu-mutedFg">
              We'll contact you at <strong>{submittedData.phone || "your number"}</strong> for a consultation
            </p>
          </div>
        </div>

        {/* Enquiry Summary */}
        {submittedData.name && (
          <div
            className="rounded-2xl p-6 mb-10 text-left animate-fade-in-up delay-400"
            style={{ background: "rgba(58, 90, 64, 0.06)", border: "1px solid rgba(58, 90, 64, 0.2)" }}
            data-testid="enquiry-summary"
          >
            <h3 className="font-heading text-xl font-semibold mb-4" style={{ color: "#3A5A40" }}>
              Enquiry Summary
            </h3>
            <div className="grid grid-cols-2 gap-3 font-body text-sm">
              <div>
                <span className="text-vastu-mutedFg">Plot Dimensions</span>
                <p className="font-semibold text-vastu-charcoal">
                  {submittedData.plot_length} x {submittedData.plot_width} ft
                </p>
              </div>
              <div>
                <span className="text-vastu-mutedFg">Facing Direction</span>
                <p className="font-semibold text-vastu-charcoal">{submittedData.facing_direction}</p>
              </div>
              <div>
                <span className="text-vastu-mutedFg">Rooms / Floors</span>
                <p className="font-semibold text-vastu-charcoal">
                  {submittedData.num_rooms} Rooms, {submittedData.num_floors} Floor{submittedData.num_floors > 1 ? "s" : ""}
                </p>
              </div>
              <div>
                <span className="text-vastu-mutedFg">Location</span>
                <p className="font-semibold text-vastu-charcoal">{submittedData.location}</p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-500">
          <Link to="/" className="btn-primary" data-testid="back-home-btn">
            <Home size={18} />
            Back to Home
          </Link>
          <Link to="/get-floor-plan" className="btn-secondary" data-testid="new-enquiry-btn">
            Submit Another Enquiry
            <ArrowRight size={18} />
          </Link>
        </div>

        {/* Vastu Tip */}
        <div
          className="mt-12 rounded-2xl p-6 animate-fade-in-up delay-600"
          style={{ background: "linear-gradient(135deg, rgba(233, 196, 106, 0.15), rgba(210, 124, 44, 0.08))", border: "1px solid rgba(233, 196, 106, 0.4)" }}
        >
          <p className="section-eyebrow mb-2">Daily Vastu Tip</p>
          <p className="font-body text-sm text-vastu-mutedFg italic">
            "Place your main entrance in the North-East direction to invite prosperity and positive energy 
            into your home. The North-East (Ishaan) is ruled by Lord Shiva and is considered the most 
            auspicious direction for the main door."
          </p>
        </div>
      </div>

      {/* Simple Footer */}
      <div className="w-full py-6 border-t border-vastu-border text-center">
        <p className="font-body text-sm text-vastu-mutedFg">
          © 2026 VastuNet. All rights reserved. |{" "}
          <span style={{ color: "#D27C2C" }}>Harmonizing Spaces, Enriching Lives</span>
        </p>
      </div>
    </div>
  );
}
